/**
 * Calendar Events Hook
 * Feature: 005-clinic-scheduling (T018)
 *
 * Fetches sessions within a date range and transforms them to FullCalendar events.
 */

import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import {
  SESSION_STATUS_COLORS,
  type CalendarEvent,
  type CalendarDateRange,
  type SessionStatus,
} from '../types/scheduling.types'

// ============================================================
// Transformer
// ============================================================

interface RawSessionRow {
  id: string
  patient_id: string
  professional_id: string
  session_date: string
  start_time: string | null
  end_time: string | null
  session_type: string
  status: string
  location_text: string | null
  notes: string | null
  recurring_schedule_id: string | null
  is_exception: boolean
  patient: { full_name: string } | null
  primary_professional: { id: string; full_name: string } | null
  additional_professionals: Array<{
    professional_id: string
    professional: { id: string; full_name: string } | null
  }>
}

interface SessionProfessionalRow {
  session: RawSessionRow | null
}

const SESSION_CALENDAR_SELECT = `
  id,
  patient_id,
  professional_id,
  session_date,
  start_time,
  end_time,
  session_type,
  status,
  location_text,
  notes,
  recurring_schedule_id,
  is_exception,
  patient:patients!sessions_patient_id_fkey!inner (full_name, status),
  primary_professional:profiles!sessions_professional_id_fkey (
    id,
    full_name
  ),
  additional_professionals:session_professionals (
    professional_id,
    professional:profiles!session_professionals_professional_fkey (
      id,
      full_name
    )
  )
`

function sessionToCalendarEvent(
  session: RawSessionRow,
  whatsappAdminRequestCount = 0
): CalendarEvent | null {
  if (!session.start_time || !session.end_time) return null

  const status = session.status as SessionStatus
  const colors =
    SESSION_STATUS_COLORS[status] ?? SESSION_STATUS_COLORS.scheduled

  const patientName = session.patient?.full_name ?? 'Paciente'
  const title =
    whatsappAdminRequestCount > 0 ? `${patientName} • WhatsApp` : patientName

  const professionalIds = [session.professional_id]
  const professionalNames: string[] = []
  if (session.primary_professional) {
    professionalNames.push(session.primary_professional.full_name)
  }
  for (const ap of session.additional_professionals ?? []) {
    professionalIds.push(ap.professional_id)
    if (ap.professional) {
      professionalNames.push(ap.professional.full_name)
    }
  }

  return {
    id: session.id,
    title,
    start: `${session.session_date}T${session.start_time}`,
    end: `${session.session_date}T${session.end_time}`,
    allDay: false as const,
    backgroundColor: colors.bg,
    borderColor: colors.border,
    textColor: colors.text,
    extendedProps: {
      session_id: session.id,
      patient_id: session.patient_id,
      patient_name: patientName,
      professional_ids: professionalIds,
      professional_names: professionalNames,
      status,
      session_type: session.session_type,
      is_recurring: !!session.recurring_schedule_id,
      recurring_schedule_id: session.recurring_schedule_id,
      is_exception: session.is_exception,
      location_text: session.location_text,
      notes: session.notes,
      whatsapp_admin_request_count: whatsappAdminRequestCount,
    },
  }
}

// ============================================================
// Fetch Function
// ============================================================

async function fetchCalendarEvents(
  organizationId: string,
  dateRange: CalendarDateRange,
  professionalId?: string | null
): Promise<CalendarEvent[]> {
  let primaryQuery = supabase
    .from('sessions')
    .select(SESSION_CALENDAR_SELECT)
    .eq('organization_id', organizationId)
    .neq('patient.status', 'deleted')
    .gte('session_date', dateRange.start)
    .lte('session_date', dateRange.end)

  if (professionalId) {
    primaryQuery = primaryQuery.eq('professional_id', professionalId)
  }

  const primaryPromise = primaryQuery

  const additionalPromise = professionalId
    ? supabase
        .from('session_professionals')
        .select(`session:sessions!inner (${SESSION_CALENDAR_SELECT})`)
        .eq('professional_id', professionalId)
        .eq('session.organization_id', organizationId)
        .gte('session.session_date', dateRange.start)
        .lte('session.session_date', dateRange.end)
    : Promise.resolve({ data: [], error: null })

  const [
    { data: primaryRows, error: primaryError },
    { data: additionalRows, error: additionalError },
  ] = await Promise.all([primaryPromise, additionalPromise])

  if (primaryError) throw primaryError
  if (additionalError) throw additionalError

  const sessionsById = new Map<string, RawSessionRow>()

  for (const row of primaryRows ?? []) {
    const session = row as unknown as RawSessionRow
    sessionsById.set(session.id, session)
  }

  for (const row of additionalRows ?? []) {
    const session = (row as unknown as SessionProfessionalRow).session
    if (!session) continue
    sessionsById.set(session.id, session)
  }

  const requestCounts = new Map<string, number>()
  const sessionIds = Array.from(sessionsById.keys())
  if (sessionIds.length > 0) {
    const { data: requestRows, error: requestError } = await supabase
      .from('whatsapp_admin_requests')
      .select('session_id')
      .eq('organization_id', organizationId)
      .eq('status', 'open')
      .in('session_id', sessionIds)

    if (requestError) throw requestError

    for (const row of requestRows ?? []) {
      const sessionId = (row as { session_id: string | null }).session_id
      if (!sessionId) continue
      requestCounts.set(sessionId, (requestCounts.get(sessionId) ?? 0) + 1)
    }
  }

  return Array.from(sessionsById.values())
    .sort((a, b) =>
      `${a.session_date}T${a.start_time ?? ''}`.localeCompare(
        `${b.session_date}T${b.start_time ?? ''}`
      )
    )
    .map((row) => sessionToCalendarEvent(row, requestCounts.get(row.id) ?? 0))
    .filter((event): event is CalendarEvent => event !== null)
}

// ============================================================
// Hook
// ============================================================

export function useCalendarEvents() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  const [dateRange, setDateRange] = useState<CalendarDateRange>({
    start: '',
    end: '',
  })
  const [professionalFilter, setProfessionalFilter] = useState<string | null>(
    null
  )

  const query = useQuery({
    queryKey: [
      'calendar-events',
      organizationId,
      dateRange.start,
      dateRange.end,
      professionalFilter,
    ],
    queryFn: () =>
      fetchCalendarEvents(organizationId!, dateRange, professionalFilter),
    enabled: !!organizationId && !!dateRange.start && !!dateRange.end,
  })

  const handleSetDateRange = useCallback((range: CalendarDateRange) => {
    setDateRange(range)
  }, [])

  const handleSetProfessionalFilter = useCallback((id: string | null) => {
    setProfessionalFilter(id)
  }, [])

  return {
    events: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    setDateRange: handleSetDateRange,
    setProfessionalFilter: handleSetProfessionalFilter,
    professionalFilter,
  }
}
